import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { type ReactNode, useMemo } from 'react'

import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'
import { CURRENT_DIALOGUES_PEER_ATTRIBUTE_TAGS_ORDERED_VALUES } from '@/client/services/learnerFeedback/constants'

import DialoguesPeerTagsFormInput from '@/components/LearnerFeedback/DialoguesPeerTagsFormInput'
import Alert from '@/components/UI/Alert'
import type { FormWizardV2Step } from '@/components/UI/FormWizardV2/types'
import UserAvatar from '@/components/UserAvatar'

const DIALOGUES_PEER_COMMENT_PLACEHOLDER = 'I appreciated how my partner...'
const DIALOGUES_SELF_COMMENT_PLACEHOLDER =
  'I learned that to engage in dialogue more effectively, I can work on...'
const DIALOGUES_SELF_ROWS = 3

export default function useDialoguesReflectionFormInputs({
  allPartners,
}: {
  viewOnly?: boolean
  allPartners: Query[]
}): {
  steps: FormWizardV2Step<string>[]
} {
  const ContainerComponent = useMemo(
    () =>
      ({ title: _title, children }: { title: string; children: ReactNode }) => <>{children}</>,
    []
  )

  const steps: FormWizardV2Step<string>[] = useMemo(() => {
    const partnerSteps: FormWizardV2Step<string>[] = allPartners.map(u => {
      const id = u(UserQ.id) ?? 0
      const userDisplayName = u(UserQ.display_name) ?? ''
      const avatarUrl = u(UserQ.avatar_url) ?? ''
      const progressionLevel = u(UserQ.progression_level)

      return {
        slug: `peer-${id}`,
        title: 'Conversation Partner Reflections',
        formInputs: [
          {
            display: 'display',
            type: 'content',
            name: `peer-${id}-content`,
            size: 6,
            content: (
              <Box borderWidth="1px" borderRadius="8px" p="12px">
                <Text fontSize="xl" mb="12px">
                  Conversation Partner Reflections
                </Text>
                <Alert variant="info" mb="12px">
                  Partners will have option to include on their portfolios. Please do not mention
                  specific views on topic.
                </Alert>
                <Flex align="center" gap="12px" mt="24px">
                  <Text fontSize="lg">Complete your reflection for</Text>
                  <UserAvatar
                    name={userDisplayName}
                    src={avatarUrl}
                    size="md"
                    progressionLevel={progressionLevel}
                  />
                  <Text fontWeight="700">{userDisplayName}</Text>
                </Flex>
              </Box>
            ),
          },
          {
            display: 'input',
            type: 'multi-select',
            name: `peer-${id}-tags`,
            size: 6,
            label: 'Select up to 5 terms for partner’s strengths',
            options: CURRENT_DIALOGUES_PEER_ATTRIBUTE_TAGS_ORDERED_VALUES.map(tag => ({
              label: tag,
              value: tag,
            })),
            required: true,
            Component: ({ input }) => <DialoguesPeerTagsFormInput input={input} />,
          },
          {
            display: 'input',
            type: 'textarea',
            name: `peer-${id}-comment`,
            size: 6,
            label:
              'What did you most appreciate about how this partner participated in the conversation?',
            placeholder: DIALOGUES_PEER_COMMENT_PLACEHOLDER,
            required: true,
            rows: 5,
          },
        ],
        ContainerComponent,
      }
    })

    const selfStep: FormWizardV2Step<string> = {
      slug: 'self',
      title: 'Self Reflection for Portfolio',
      formInputs: [
        {
          display: 'display',
          type: 'content',
          name: 'self-content',
          size: 6,
          content: (
            <Stack>
              <Text fontSize="xl" mb="12px">
                Self Reflection for Portfolio
              </Text>
              <Alert variant="info" mb="12px">
                Remember you can choose to highlight self reflections on your Dialogues Portfolio.
                Do not include your specific views in your reflection.
              </Alert>
            </Stack>
          ),
        },
        {
          display: 'input',
          type: 'textarea',
          name: 'self-comment',
          size: 6,
          label: 'How did this conversation help you learn and grow?',
          placeholder: DIALOGUES_SELF_COMMENT_PLACEHOLDER,
          required: true,
          rows: DIALOGUES_SELF_ROWS,
        },
      ],
      ContainerComponent,
    }

    return [...partnerSteps, selfStep]
  }, [allPartners, ContainerComponent])

  return { steps }
}
